import { useCallback, useRef } from 'react';

import type { HumanMessageIdReplacementListener } from './types';
import { userSubmissionLifecycle } from './user-submission-lifecycle';

type HumanMessageIdReplacement = {
	onHumanMessageIdReplaced: (conversationId: string, oldId: string, newId: string) => void;
	registerHumanMessageIdReplacementHandler: (
		handler: HumanMessageIdReplacementListener,
	) => () => void;
};

/** Keeps retained submission identity and the mounted detector listener on the same ID replacement path. */
export const useHumanMessageIdReplacement = (): HumanMessageIdReplacement => {
	const handlerRef = useRef<HumanMessageIdReplacementListener | undefined>();
	const registerHumanMessageIdReplacementHandler = useCallback(
		(handler: HumanMessageIdReplacementListener): (() => void) => {
			handlerRef.current = handler;

			return () => {
				if (handlerRef.current === handler) {
					handlerRef.current = undefined;
				}
			};
		},
		[],
	);
	const onHumanMessageIdReplaced = useCallback(
		(conversationId: string, oldId: string, newId: string): void => {
			userSubmissionLifecycle.replaceHumanMessageId({ conversationId, oldId, newId });
			handlerRef.current?.({ conversationId, oldId, newId });
		},
		[],
	);

	return { onHumanMessageIdReplaced, registerHumanMessageIdReplacementHandler };
};
